
import { useState, useEffect, useCallback } from 'react';

interface LightboxProps {
  images: { url: string; title: string }[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function Lightbox({ images, currentIndex, isOpen, onClose, onNavigate }: LightboxProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  const handlePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    onNavigate((currentIndex - 1 + images.length) % images.length);
    setTimeout(() => setIsAnimating(false), 300);
  }, [currentIndex, images.length, onNavigate, isAnimating]);

  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    onNavigate((currentIndex + 1) % images.length);
    setTimeout(() => setIsAnimating(false), 300);
  }, [currentIndex, images.length, onNavigate, isAnimating]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handlePrev, handleNext, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <p className="text-white/70 text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </p>
        <p className="text-white text-sm font-medium hidden md:block">{images[currentIndex].title}</p>
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl text-white"></i>
        </button>
      </div>

      {/* Main image */}
      <div className="flex-1 flex items-center justify-center px-16 relative">
        <button
          onClick={handlePrev}
          className="absolute left-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer z-10"
        >
          <i className="ri-arrow-left-s-line text-2xl text-white"></i>
        </button>

        <div className="max-w-5xl max-h-[75vh] w-full h-full flex items-center justify-center">
          <img
            src={images[currentIndex].url}
            alt={images[currentIndex].title}
            className="max-w-full max-h-[75vh] object-contain rounded-lg transition-opacity duration-300"
          />
        </div>

        <button
          onClick={handleNext}
          className="absolute right-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer z-10"
        >
          <i className="ri-arrow-right-s-line text-2xl text-white"></i>
        </button>
      </div>

      {/* Thumbnail strip */}
      <div className="px-6 py-4 overflow-x-auto">
        <div className="flex items-center gap-2 justify-center">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => onNavigate(idx)}
              className={`w-16 h-12 rounded-md overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-200 ${
                idx === currentIndex ? 'ring-2 ring-white opacity-100 scale-105' : 'opacity-40 hover:opacity-70'
              }`}
            >
              <img src={img.url} alt={img.title} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
